import React, { useEffect, useState, useCallback } from 'react';
import Icon from '../../ui/Icon';
import { T } from '../../constants/mockData';
import { attendanceAPI, usersAPI } from '../../services/api';
import { formatInTz } from '../../utils/timezone';
import { toast } from '../../utils/toast';

const WEEKDAYS = [
  { i: 0, name: 'Sunday' },
  { i: 1, name: 'Monday' },
  { i: 2, name: 'Tuesday' },
  { i: 3, name: 'Wednesday' },
  { i: 4, name: 'Thursday' },
  { i: 5, name: 'Friday' },
  { i: 6, name: 'Saturday' },
];

const fmtDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    return formatInTz(dt, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return String(d); }
};
const fmtTime = (d) => {
  if (!d) return '—';
  try { return formatInTz(d, { hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};
const fmtDuration = (secs) => {
  if (!secs || secs < 0) return '0h 00m';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const inputStyle = {
  height: 34, padding: '0 10px', borderRadius: 8, border: `1px solid ${T.border}`,
  fontSize: 13, fontFamily: 'inherit', background: '#fff', color: '#1A202C',
};
const btnPrimary = {
  height: 34, padding: '0 14px', borderRadius: 8, background: T.active,
  color: '#fff', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
  fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
};
const btnGhost = {
  height: 32, padding: '0 12px', borderRadius: 8, background: '#fff',
  color: T.active, fontSize: 13, fontWeight: 600, border: `1px solid ${T.border}`,
  cursor: 'pointer', fontFamily: 'inherit',
};
const cardStyle = { background: '#fff', border: `1px solid ${T.border}`, borderRadius: 14, padding: 18 };
const thS = { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 700, color: T.muted, borderBottom: `1px solid ${T.border}`, background: '#F7F9FC' };
const tdS = { padding: '10px 12px', fontSize: 13, color: '#1A202C', borderBottom: `1px solid ${T.border}` };

export default function AttendancePage({ mode = 'all' }) {
  const ALL_TABS = [
    { id: 'punch',     label: 'My Attendance',  modes: ['all', 'self'] },
    { id: 'holidays',  label: 'Public Holidays', modes: ['all', 'settings'] },
    { id: 'schedule',  label: 'Work Schedule',   modes: ['all', 'settings'] },
    { id: 'leaves',    label: 'Leaves',          modes: ['all', 'settings'] },
    { id: 'logs',      label: 'Attendance Log',  modes: ['all', 'settings'] },
    { id: 'report',    label: 'Reports',         modes: ['all', 'settings'] },
  ];
  const tabs = ALL_TABS.filter(t => t.modes.includes(mode));
  const [tab, setTab] = useState(tabs[0]?.id || 'punch');

  // Reset to first valid tab whenever mode changes (e.g. Attendance → Attendance Settings)
  useEffect(() => {
    const validTabs = ALL_TABS.filter(t => t.modes.includes(mode));
    const firstId = validTabs[0]?.id || 'punch';
    setTab(prev => validTabs.some(t => t.id === prev) ? prev : firstId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const heading = mode === 'self' ? 'Attendance'
                : mode === 'settings' ? 'Attendance Settings'
                : 'Attendance';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#1A202C' }}>{heading}</div>
      </div>
      {tabs.length > 1 && (
        <div style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${T.border}` }}>
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                color: tab === t.id ? T.active : T.muted,
                borderBottom: `2px solid ${tab === t.id ? T.active : 'transparent'}`,
                marginBottom: -1,
              }}
            >{t.label}</button>
          ))}
        </div>
      )}
      {tab === 'punch'    && <PunchSection />}
      {tab === 'holidays' && <HolidaysSection />}
      {tab === 'schedule' && <ScheduleSection />}
      {tab === 'leaves'   && <LeavesSection />}
      {tab === 'logs'     && <LogsSection />}
      {tab === 'report'   && <ReportSection />}
    </div>
  );
}

// ─── My Attendance (Punch In/Out) ────────────────────────────────────────────
function PunchSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const refresh = useCallback(async () => {
    try {
      const s = await attendanceAPI.myStatus();
      setStatus(s);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const open = status?.open_session;
  const liveSecs = open ? Math.floor((now - new Date(open.punch_in).getTime()) / 1000) : 0;
  // Sum completed sessions + live duration of the open session (avoids double-counting
  // since today_seconds from the API already includes a snapshot of the open session)
  const completedSecs = (status?.today_sessions || [])
    .filter(s => s.total_seconds != null)
    .reduce((sum, s) => sum + Number(s.total_seconds), 0);
  const totalToday = completedSecs + liveSecs;

  const doPunchIn = async () => {
    setBusy(true);
    try { await attendanceAPI.punchIn(); await refresh(); }
    catch (e) { toast(e.message); }
    setBusy(false);
  };
  const doPunchOut = async () => {
    setBusy(true);
    try { await attendanceAPI.punchOut(); await refresh(); }
    catch (e) { toast(e.message); }
    setBusy(false);
  };

  if (loading) return <div style={cardStyle}>Loading…</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1A202C', marginBottom: 14 }}>Today</div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '14px 0' }}>
          <div style={{ fontSize: 13, color: T.muted }}>{fmtDate(new Date())}</div>
          <div style={{ fontSize: 40, fontWeight: 800, color: open ? '#10B981' : '#1A202C', fontVariantNumeric: 'tabular-nums' }}>
            {fmtDuration(totalToday)}
          </div>
          <div style={{ fontSize: 12, color: T.muted }}>
            {open
              ? `Punched in at ${fmtTime(open.punch_in)}`
              : (status?.today_sessions?.length ? 'You are punched out.' : 'Not punched in yet.')}
          </div>
          {open ? (
            <button onClick={doPunchOut} disabled={busy} style={{ ...btnPrimary, background: '#EF4444', height: 44, padding: '0 28px', fontSize: 15 }}>
              <Icon name="logout" size={16} color="#fff" /> Punch Out
            </button>
          ) : (
            <button onClick={doPunchIn} disabled={busy} style={{ ...btnPrimary, background: '#10B981', height: 44, padding: '0 28px', fontSize: 15 }}>
              <Icon name="login" size={16} color="#fff" /> Punch In
            </button>
          )}
        </div>
      </div>
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1A202C', marginBottom: 10 }}>Today's Sessions</div>
        {(status?.today_sessions || []).length === 0 ? (
          <div style={{ fontSize: 13, color: T.muted, padding: '24px 0', textAlign: 'center' }}>No sessions yet today.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thS}>Punch In</th><th style={thS}>Punch Out</th><th style={thS}>Duration</th></tr></thead>
            <tbody>
              {status.today_sessions.map(s => (
                <tr key={s.id}>
                  <td style={tdS}>{fmtTime(s.punch_in)}</td>
                  <td style={tdS}>{s.punch_out ? fmtTime(s.punch_out) : <span style={{ color: '#10B981', fontWeight: 600 }}>Active</span>}</td>
                  <td style={tdS}>{fmtDuration(s.total_seconds || (s.punch_out ? 0 : liveSecs))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Public Holidays ─────────────────────────────────────────────────────────
function HolidaysSection() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ holiday_date: '', name: '', description: '' });

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setRows(await attendanceAPI.listHolidays(year)); }
    catch (e) { console.error(e); }
    setLoading(false);
  }, [year]);
  useEffect(() => { refresh(); }, [refresh]);

  const openAdd = () => { setEditing(null); setForm({ holiday_date: '', name: '', description: '' }); setShowAdd(true); };
  const openEdit = (h) => {
    setEditing(h);
    setForm({ holiday_date: (h.holiday_date || '').slice(0, 10), name: h.name || '', description: h.description || '' });
    setShowAdd(true);
  };
  const save = async () => {
    if (!form.holiday_date || !form.name.trim()) { toast('Date and name are required.', 'warning'); return; }
    try {
      if (editing) await attendanceAPI.updateHoliday(editing.id, form);
      else         await attendanceAPI.createHoliday(form);
      setShowAdd(false); refresh();
    } catch (e) { toast(e.message); }
  };
  const del = async (h) => {
    if (!window.confirm(`Delete holiday "${h.name}"?`)) return;
    try { await attendanceAPI.deleteHoliday(h.id); refresh(); }
    catch (e) { toast(e.message); }
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Public Holidays</div>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...inputStyle, width: 110 }}>
            {[0, 1, 2, -1, -2].map(o => {
              const y = new Date().getFullYear() + o;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        </div>
        <button onClick={openAdd} style={btnPrimary}>
          <Icon name="plus" size={14} color="#fff" /> Add Holiday
        </button>
      </div>
      {loading ? <div style={{ color: T.muted }}>Loading…</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={thS}>Date</th><th style={thS}>Day</th><th style={thS}>Name</th><th style={thS}>Description</th><th style={thS}></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} style={{ ...tdS, textAlign: 'center', color: T.muted }}>No holidays for {year}.</td></tr>
            ) : rows.map(h => (
              <tr key={h.id}>
                <td style={tdS}>{fmtDate(h.holiday_date)}</td>
                <td style={tdS}>{new Date(h.holiday_date).toLocaleDateString('en-GB', { weekday: 'long' })}</td>
                <td style={{ ...tdS, fontWeight: 600 }}>{h.name}</td>
                <td style={{ ...tdS, color: T.muted }}>{h.description || '—'}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>
                  <button onClick={() => openEdit(h)} style={{ ...btnGhost, marginRight: 6 }}>Edit</button>
                  <button onClick={() => del(h)} style={{ ...btnGhost, color: '#EF4444', borderColor: '#FCA5A5' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showAdd && (
        <Modal title={editing ? 'Edit Holiday' : 'Add Holiday'} onClose={() => setShowAdd(false)} onSave={save}>
          <Field label="Date">
            <input type="date" value={form.holiday_date} onChange={e => setForm(f => ({ ...f, holiday_date: e.target.value }))} style={inputStyle} />
          </Field>
          <Field label="Name">
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., Good Friday" style={inputStyle} />
          </Field>
          <Field label="Description (optional)">
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={inputStyle} />
          </Field>
        </Modal>
      )}
    </div>
  );
}

// ─── Work Schedule ───────────────────────────────────────────────────────────
function ScheduleSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await attendanceAPI.getSchedule();
      const byDay = Object.fromEntries((data || []).map(d => [d.day_of_week, d]));
      const seeded = WEEKDAYS.map(w => byDay[w.i] || {
        day_of_week: w.i, hours: 0, is_working_day: false, start_time: '09:00', end_time: '17:00',
      });
      setItems(seeded);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const update = (i, patch) => setItems(prev => prev.map((row, idx) => idx === i ? { ...row, ...patch } : row));

  const save = async () => {
    setSaving(true);
    try {
      await attendanceAPI.updateSchedule(items.map(r => ({
        day_of_week:    r.day_of_week,
        hours:          Number(r.hours) || 0,
        is_working_day: !!r.is_working_day,
        start_time:     r.start_time || null,
        end_time:       r.end_time   || null,
      })));
      await refresh();
    } catch (e) { toast(e.message); }
    setSaving(false);
  };

  const total = items.filter(r => r.is_working_day).reduce((s, r) => s + (Number(r.hours) || 0), 0);

  if (loading) return <div style={cardStyle}>Loading…</div>;

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Weekly Work Schedule</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>Standard hours per weekday used to compute scheduled hours per month.</div>
        </div>
        <div style={{ fontSize: 13, color: T.muted }}>
          Weekly total: <span style={{ fontWeight: 700, color: '#1A202C' }}>{total.toFixed(1)}h</span>
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={thS}>Day</th><th style={thS}>Working?</th><th style={thS}>Hours</th>
          <th style={thS}>Start</th><th style={thS}>End</th>
        </tr></thead>
        <tbody>
          {items.map((row, i) => (
            <tr key={row.day_of_week}>
              <td style={{ ...tdS, fontWeight: 600 }}>{WEEKDAYS[row.day_of_week].name}</td>
              <td style={tdS}>
                <input type="checkbox" checked={!!row.is_working_day} onChange={e => update(i, { is_working_day: e.target.checked })} />
              </td>
              <td style={tdS}>
                <input type="number" step="0.5" min="0" max="24" value={row.hours} onChange={e => update(i, { hours: e.target.value })} style={{ ...inputStyle, width: 90 }} disabled={!row.is_working_day} />
              </td>
              <td style={tdS}>
                <input type="time" value={row.start_time || ''} onChange={e => update(i, { start_time: e.target.value })} style={{ ...inputStyle, width: 110 }} disabled={!row.is_working_day} />
              </td>
              <td style={tdS}>
                <input type="time" value={row.end_time || ''} onChange={e => update(i, { end_time: e.target.value })} style={{ ...inputStyle, width: 110 }} disabled={!row.is_working_day} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={save} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save Schedule'}</button>
      </div>
    </div>
  );
}

// ─── Leaves ──────────────────────────────────────────────────────────────────
function LeavesSection() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ user_id: '', leave_date: todayISO(), leave_type: 'full', status: 'approved', reason: '', notes: '' });

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setRows(await attendanceAPI.listLeaves(filterUser ? { user_id: filterUser } : {})); }
    catch (e) { console.error(e); }
    setLoading(false);
  }, [filterUser]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { usersAPI.list().then(u => setUsers(u || [])).catch(() => {}); }, []);

  const openAdd = () => { setEditing(null); setForm({ user_id: '', leave_date: todayISO(), leave_type: 'full', status: 'approved', reason: '', notes: '' }); setShowAdd(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({
      user_id: l.user_id || '',
      leave_date: (l.leave_date || '').slice(0, 10),
      leave_type: l.leave_type || 'full',
      status: l.status || 'approved',
      reason: l.reason || '',
      notes: l.notes || '',
    });
    setShowAdd(true);
  };
  const save = async () => {
    if (!form.user_id || !form.leave_date) { toast('User and date are required.', 'warning'); return; }
    try {
      if (editing) await attendanceAPI.updateLeave(editing.id, form);
      else         await attendanceAPI.createLeave(form);
      setShowAdd(false); refresh();
    } catch (e) { toast(e.message); }
  };
  const del = async (l) => {
    if (!window.confirm('Delete this leave entry?')) return;
    try { await attendanceAPI.deleteLeave(l.id); refresh(); }
    catch (e) { toast(e.message); }
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Leaves</div>
          <select value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ ...inputStyle, width: 220 }}>
            <option value="">All users</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <button onClick={openAdd} style={btnPrimary}>
          <Icon name="plus" size={14} color="#fff" /> Add Leave
        </button>
      </div>
      {loading ? <div style={{ color: T.muted }}>Loading…</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={thS}>User</th><th style={thS}>Date</th><th style={thS}>Type</th>
            <th style={thS}>Status</th><th style={thS}>Reason</th><th style={thS}></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} style={{ ...tdS, textAlign: 'center', color: T.muted }}>No leaves recorded.</td></tr>
            ) : rows.map(l => (
              <tr key={l.id}>
                <td style={tdS}>{l.user_name || users.find(u => u.id === l.user_id)?.name || `#${l.user_id}`}</td>
                <td style={tdS}>{fmtDate(l.leave_date)}</td>
                <td style={tdS}>{l.leave_type}</td>
                <td style={tdS}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                    background: l.status === 'approved' ? '#D1FAE5' : l.status === 'rejected' ? '#FEE2E2' : '#FEF3C7',
                    color: l.status === 'approved' ? '#065F46' : l.status === 'rejected' ? '#991B1B' : '#92400E',
                  }}>{l.status}</span>
                </td>
                <td style={{ ...tdS, color: T.muted }}>{l.reason || '—'}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>
                  <button onClick={() => openEdit(l)} style={{ ...btnGhost, marginRight: 6 }}>Edit</button>
                  <button onClick={() => del(l)} style={{ ...btnGhost, color: '#EF4444', borderColor: '#FCA5A5' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showAdd && (
        <Modal title={editing ? 'Edit Leave' : 'Add Leave'} onClose={() => setShowAdd(false)} onSave={save}>
          <Field label="User">
            <select value={form.user_id} onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))} style={inputStyle}>
              <option value="">Select user…</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <Field label="Date">
            <input type="date" value={form.leave_date} onChange={e => setForm(f => ({ ...f, leave_date: e.target.value }))} style={inputStyle} />
          </Field>
          <Field label="Type">
            <select value={form.leave_type} onChange={e => setForm(f => ({ ...f, leave_type: e.target.value }))} style={inputStyle}>
              <option value="full">Full Day</option>
              <option value="half_am">Half Day (AM)</option>
              <option value="half_pm">Half Day (PM)</option>
            </select>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </Field>
          <Field label="Reason">
            <input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} style={inputStyle} />
          </Field>
          <Field label="Notes">
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={inputStyle} />
          </Field>
        </Modal>
      )}
    </div>
  );
}

// ─── Attendance Log (admin view of punch in/out) ─────────────────────────────
function LogsSection() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo]     = useState('');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterUser) params.user_id = filterUser;
      if (from) params.date_from = from;
      if (to)   params.date_to = to;
      setRows(await attendanceAPI.logs(params));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterUser, from, to]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { usersAPI.list().then(u => setUsers(u || [])).catch(() => {}); }, []);

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginRight: 8 }}>Attendance Log</div>
        <select value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ ...inputStyle, width: 200 }}>
          <option value="">All users</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
        <span style={{ color: T.muted, fontSize: 12 }}>to</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
      </div>
      {loading ? <div style={{ color: T.muted }}>Loading…</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={thS}>Date</th><th style={thS}>User</th>
            <th style={thS}>Punch In</th><th style={thS}>Punch Out</th><th style={thS}>Duration</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} style={{ ...tdS, textAlign: 'center', color: T.muted }}>No attendance records.</td></tr>
            ) : rows.map(r => (
              <tr key={r.id}>
                <td style={tdS}>{fmtDate(r.punch_date)}</td>
                <td style={tdS}>{r.user_name}</td>
                <td style={tdS}>{fmtTime(r.punch_in)}</td>
                <td style={tdS}>{r.punch_out ? fmtTime(r.punch_out) : <span style={{ color: '#10B981', fontWeight: 600 }}>Active</span>}</td>
                <td style={tdS}>{fmtDuration(r.total_seconds)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Report Section ──────────────────────────────────────────────────────────
function ReportSection() {
  const now = new Date();
  const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const todayStr = now.toISOString().slice(0, 10);

  const [from, setFrom]     = useState(firstOfMonth);
  const [to, setTo]         = useState(todayStr);
  const [users, setUsers]   = useState([]);
  const [filterUser, setFilterUser] = useState('');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('detail'); // 'detail' | 'summary'

  useEffect(() => {
    usersAPI.list().then(u => setUsers((u || []).filter(u => u.role !== 'requester'))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (from)       params.date_from = from;
      if (to)         params.date_to   = to;
      if (filterUser) params.user_id   = filterUser;
      const result = await attendanceAPI.report(params);
      setData(result);
    } catch (e) { console.error(e); toast('Failed to load report.'); }
    setLoading(false);
  }, [from, to, filterUser]);

  // Status badge colours
  const statusColor = s => ({
    Present: '#10B981', Partial: '#F59E0B', Absent: '#EF4444',
    Holiday: '#6366F1', 'Non-Working': '#9CA3AF', Active: '#3B82F6',
  }[s] || '#9CA3AF');

  // ── Excel export (SpreadsheetML / XML-based .xls that Excel can open) ──────
  const exportExcel = () => {
    if (!data) return;
    const rows = viewMode === 'summary' ? data.summary : data.rows;
    if (!rows || rows.length === 0) { toast('No data to export.', 'info'); return; }

    let header, getRow;
    if (viewMode === 'summary') {
      header = ['Employee', 'Email', 'Department', 'Role', 'Total Days', 'Present', 'Absent', 'Partial', 'Total Hours', 'Late Count', 'Early-Out Count'];
      getRow = r => [r.user_name, r.user_email, r.department, r.role, r.total_days, r.present_days, r.absent_days, r.partial_days, Number(r.total_hours).toFixed(2), r.late_count, r.early_out_count];
    } else {
      header = ['Employee', 'Email', 'Department', 'Date', 'Day', 'First Punch In', 'Last Punch Out', 'Total Hours', 'Expected Hours', 'Sessions', 'Late (min)', 'Early Out (min)', 'Status'];
      getRow = r => {
        const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
        const dayLabel = r.date ? dayNames[new Date(r.date).getDay()] : '';
        return [
          r.user_name, r.user_email, r.department, r.date, dayLabel,
          r.first_in  ? formatInTz(r.first_in, { hour: '2-digit', minute: '2-digit' }) : '',
          r.last_out  ? formatInTz(r.last_out, { hour: '2-digit', minute: '2-digit' }) : (r.status === 'Active' ? 'Active' : ''),
          Number(r.total_hours).toFixed(2),
          r.expected_hours,
          r.session_count,
          r.late_minutes,
          r.early_out_minutes,
          r.status,
        ];
      };
    }

    // Build SpreadsheetML XML
    const esc = v => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const cell = v => `<Cell><Data ss:Type="${typeof v === 'number' ? 'Number' : 'String'}">${esc(v)}</Data></Cell>`;
    const rowXml = cols => `<Row>${cols.map(cell).join('')}</Row>`;
    const xml = [
      `<?xml version="1.0"?>`,
      `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">`,
      `<Worksheet ss:Name="Attendance Report"><Table>`,
      rowXml(header),
      ...rows.map(r => rowXml(getRow(r))),
      `</Table></Worksheet></Workbook>`,
    ].join('\n');

    const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `Attendance_Report_${from}_to_${to}${viewMode === 'summary' ? '_summary' : ''}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── CSV export ──────────────────────────────────────────────────────────────
  const exportCSV = () => {
    if (!data) return;
    const rows = viewMode === 'summary' ? data.summary : data.rows;
    if (!rows || rows.length === 0) { toast('No data to export.', 'info'); return; }
    let lines, header, getRow;
    if (viewMode === 'summary') {
      header = ['Employee','Email','Department','Role','Total Days','Present','Absent','Partial','Total Hours','Late Count','Early-Out Count'];
      getRow = r => [r.user_name, r.user_email, r.department, r.role, r.total_days, r.present_days, r.absent_days, r.partial_days, Number(r.total_hours).toFixed(2), r.late_count, r.early_out_count];
    } else {
      const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      header = ['Employee','Email','Department','Date','Day','First In','Last Out','Total Hours','Expected Hours','Sessions','Late (min)','Early Out (min)','Status'];
      getRow = r => [
        r.user_name, r.user_email, r.department, r.date,
        r.date ? dayNames[new Date(r.date).getDay()] : '',
        r.first_in  ? formatInTz(r.first_in, { hour: '2-digit', minute: '2-digit' }) : '',
        r.last_out  ? formatInTz(r.last_out, { hour: '2-digit', minute: '2-digit' }) : (r.status === 'Active' ? 'Active' : ''),
        Number(r.total_hours).toFixed(2), r.expected_hours, r.session_count, r.late_minutes, r.early_out_minutes, r.status,
      ];
    }
    const csvEsc = v => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s; };
    lines = [header, ...rows.map(getRow)].map(row => row.map(csvEsc).join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `Attendance_Report_${from}_to_${to}${viewMode === 'summary' ? '_summary' : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Summary stats ────────────────────────────────────────────────────────────
  const stats = data ? (() => {
    const rows = data.rows || [];
    const totalPresent  = rows.filter(r => ['Present','Active','Partial'].includes(r.status)).length;
    const totalAbsent   = rows.filter(r => r.status === 'Absent').length;
    const totalHours    = rows.reduce((s, r) => s + Number(r.total_hours || 0), 0);
    const presentDays   = rows.filter(r => ['Present','Active','Partial'].includes(r.status)).length;
    const avgHours      = presentDays > 0 ? totalHours / presentDays : 0;
    return { totalPresent, totalAbsent, totalHours, avgHours };
  })() : null;

  const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Filters */}
      <div style={cardStyle}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Attendance Report</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 4 }}>FROM</div>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 4 }}>TO</div>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 4 }}>EMPLOYEE</div>
            <select value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ ...inputStyle, width: 200 }}>
              <option value="">All employees</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <button onClick={load} style={btnPrimary} disabled={loading}>
            {loading ? 'Loading…' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
          {[
            { label: 'Present Days', value: stats.totalPresent, color: '#10B981' },
            { label: 'Absent Days',  value: stats.totalAbsent,  color: '#EF4444' },
            { label: 'Total Hours',  value: stats.totalHours.toFixed(1) + 'h', color: '#3B82F6' },
            { label: 'Avg Hours/Day', value: stats.avgHours.toFixed(1) + 'h', color: '#8B5CF6' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ fontSize: 12, color: T.muted, marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      {data && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 8, padding: 3, gap: 2 }}>
              {['detail','summary'].map(m => (
                <button key={m} onClick={() => setViewMode(m)} style={{
                  padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                  background: viewMode === m ? '#fff' : 'transparent',
                  color: viewMode === m ? '#1A202C' : T.muted,
                  boxShadow: viewMode === m ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                }}>
                  {m === 'detail' ? 'Detail View' : 'Summary View'}
                </button>
              ))}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button onClick={exportExcel} style={{ ...btnGhost, display: 'flex', alignItems: 'center', gap: 5 }}>
                ⬇ Excel (.xls)
              </button>
              <button onClick={exportCSV} style={{ ...btnGhost, display: 'flex', alignItems: 'center', gap: 5 }}>
                ⬇ CSV
              </button>
            </div>
          </div>

          {viewMode === 'detail' ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={thS}>Employee</th>
                  <th style={thS}>Date</th>
                  <th style={thS}>Day</th>
                  <th style={thS}>First In</th>
                  <th style={thS}>Last Out</th>
                  <th style={thS}>Hours</th>
                  <th style={thS}>Expected</th>
                  <th style={thS}>Late</th>
                  <th style={thS}>Status</th>
                </tr></thead>
                <tbody>
                  {(data.rows || []).length === 0 ? (
                    <tr><td colSpan={9} style={{ ...tdS, textAlign: 'center', color: T.muted }}>No data. Select a range and click Generate Report.</td></tr>
                  ) : (data.rows || []).map((r, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                      <td style={tdS}>
                        <div style={{ fontWeight: 600, color: '#1A202C' }}>{r.user_name}</div>
                        {r.department && <div style={{ fontSize: 11, color: T.muted }}>{r.department}</div>}
                      </td>
                      <td style={tdS}>{r.date}</td>
                      <td style={tdS}>{r.date ? dayNames[new Date(r.date).getDay()] : ''}</td>
                      <td style={tdS}>{r.first_in ? formatInTz(r.first_in, { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td style={tdS}>
                        {r.last_out
                          ? formatInTz(r.last_out, { hour: '2-digit', minute: '2-digit' })
                          : r.status === 'Active'
                            ? <span style={{ color: '#10B981', fontWeight: 600 }}>Active</span>
                            : '—'}
                      </td>
                      <td style={tdS}>{r.total_hours ? `${r.total_hours}h` : '—'}</td>
                      <td style={tdS}>{r.expected_hours ? `${r.expected_hours}h` : '—'}</td>
                      <td style={tdS}>{r.late_minutes > 0 ? <span style={{ color: '#F59E0B', fontWeight: 600 }}>{r.late_minutes}m</span> : '—'}</td>
                      <td style={tdS}>
                        <span style={{
                          display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          background: statusColor(r.status) + '20', color: statusColor(r.status),
                        }}>{r.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={thS}>Employee</th>
                  <th style={thS}>Department</th>
                  <th style={thS}>Total Days</th>
                  <th style={thS}>Present</th>
                  <th style={thS}>Absent</th>
                  <th style={thS}>Partial</th>
                  <th style={thS}>Total Hours</th>
                  <th style={thS}>Late Count</th>
                  <th style={thS}>Early Out</th>
                </tr></thead>
                <tbody>
                  {(data.summary || []).length === 0 ? (
                    <tr><td colSpan={9} style={{ ...tdS, textAlign: 'center', color: T.muted }}>No data.</td></tr>
                  ) : (data.summary || []).map((r, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                      <td style={tdS}>
                        <div style={{ fontWeight: 600, color: '#1A202C' }}>{r.user_name}</div>
                        <div style={{ fontSize: 11, color: T.muted }}>{r.user_email}</div>
                      </td>
                      <td style={tdS}>{r.department || '—'}</td>
                      <td style={tdS}>{r.total_days}</td>
                      <td style={tdS}><span style={{ color: '#10B981', fontWeight: 600 }}>{r.present_days}</span></td>
                      <td style={tdS}><span style={{ color: '#EF4444', fontWeight: 600 }}>{r.absent_days}</span></td>
                      <td style={tdS}><span style={{ color: '#F59E0B', fontWeight: 600 }}>{r.partial_days}</span></td>
                      <td style={tdS}>{Number(r.total_hours).toFixed(1)}h</td>
                      <td style={tdS}>{r.late_count > 0 ? <span style={{ color: '#F59E0B', fontWeight: 600 }}>{r.late_count}</span> : 0}</td>
                      <td style={tdS}>{r.early_out_count > 0 ? <span style={{ color: '#F59E0B', fontWeight: 600 }}>{r.early_out_count}</span> : 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Reusable bits ───────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: T.muted }}>{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, children, onClose, onSave }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 460, background: '#fff', borderRadius: 14, padding: 22, display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C' }}>{title}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={onSave} style={btnPrimary}>Save</button>
        </div>
      </div>
    </div>
  );
}
